import { Navigate, Route, Routes } from 'react-router-dom';

import { ProtectedRoute } from './components/ProtectedRoute.js';
import { AppShell } from './components/layout/AppShell.js';
import { useAuth } from './features/auth/AuthContext.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { AnimalEditForm } from './features/animals/AnimalEditForm.js';
import { AnimalForm } from './features/animals/AnimalForm.js';
import { AnimalList } from './features/animals/AnimalList.js';
import { AnimalProfile } from './features/animals/AnimalProfile.js';
import { TripWizard } from './features/animals/TripWizard.js';
import { DashboardV1 } from './features/dashboard/DashboardV1.js';
import { AccountBooks } from './features/finance/AccountBooks.js';
import { SetupCostsScreen } from './features/finance/SetupCostsScreen.js';
import { AssetsScreen } from './features/finance/AssetsScreen.js';
import { MoneyPage } from './features/finance/MoneyPage.js';
import { PartnerMoneyForm } from './features/finance/PartnerMoneyForm.js';
import { PartnersScreen } from './features/finance/PartnersScreen.js';
import { VendorsScreen } from './features/finance/VendorsScreen.js';
import { DailyLogForm } from './features/operations/DailyLogForm.js';
import { InventoryDashboard } from './features/operations/InventoryDashboard.js';
import { MilkEntry } from './features/operations/MilkEntry.js';
import { MorePage } from './features/operations/MorePage.js';
import { RemindersCentre } from './features/operations/RemindersCentre.js';
import { AuditViewer } from './features/governance/AuditViewer.js';
import { CctvLinks } from './features/governance/CctvLinks.js';
import { MeetingsLog } from './features/governance/MeetingsLog.js';
import { QurbaniSeason } from './features/governance/QurbaniSeason.js';
import { TaskBoard } from './features/governance/TaskBoard.js';
import { ReportsHub } from './features/reports/ReportsHub.js';
import { MilkDeliveryGrid } from './features/sales/MilkDeliveryGrid.js';
import { CustomersScreen } from './features/sales/CustomersScreen.js';
import { ReceivablesScreen } from './features/sales/ReceivablesScreen.js';
import { ReconciliationScreen } from './features/sales/ReconciliationScreen.js';
import { UsersScreen } from './features/users/UsersScreen.js';
import { useTranslation } from './i18n/useTranslation.js';

/** Sends an already-signed-in partner away from the login screen. */
function LoginRoute(): JSX.Element {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <p role="status" className="p-6 text-center text-gray-500">
        {t('app.loading')}
      </p>
    );
  }
  return user ? <Navigate to="/" replace /> : <LoginPage />;
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />

      {/* Everything below the shell requires a session. The guard is cosmetic;
          the API enforces the real rules (SEC-3). */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardV1 />} />
        <Route path="/money" element={<MoneyPage />} />

        {/* Reached from the dashboard rather than the bottom bar: four
            destinations is what fits a thumb, and these are read less often
            than the balances that link to them. */}
        <Route path="/books" element={<AccountBooks />} />
        <Route path="/partners" element={<PartnersScreen />} />
        <Route path="/partners/money" element={<PartnerMoneyForm />} />
        <Route path="/vendors" element={<VendorsScreen />} />
        <Route path="/assets" element={<AssetsScreen />} />
        <Route path="/setup-costs" element={<SetupCostsScreen />} />
        <Route path="/animals" element={<AnimalList />} />
        {/* Declared before "/:id" so "trip" and "new" are never read as ids. */}
        <Route path="/animals/trip" element={<TripWizard />} />
        <Route path="/animals/new" element={<AnimalForm />} />
        <Route path="/animals/:id" element={<AnimalProfile />} />
        <Route path="/animals/:id/edit" element={<AnimalEditForm />} />
        <Route path="/more" element={<MorePage />} />
        <Route path="/operations/daily-log" element={<DailyLogForm />} />
        <Route path="/operations/milk" element={<MilkEntry />} />
        <Route path="/operations/inventory" element={<InventoryDashboard />} />
        <Route path="/operations/reminders" element={<RemindersCentre />} />
        <Route path="/operations/milk-round" element={<MilkDeliveryGrid />} />
        <Route path="/reports" element={<ReportsHub />} />
        <Route path="/receivables" element={<ReceivablesScreen />} />
        <Route path="/customers" element={<CustomersScreen />} />
        <Route path="/meetings" element={<MeetingsLog />} />
        <Route path="/tasks" element={<TaskBoard />} />
        <Route path="/audit" element={<AuditViewer />} />
        <Route path="/qurbani" element={<QurbaniSeason />} />
        <Route path="/cameras" element={<CctvLinks />} />
        <Route path="/reconcile" element={<ReconciliationScreen />} />
        {/* Readable by every partner (§1.4); the controls inside are admin-only. */}
        <Route path="/users" element={<UsersScreen />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
